import {
  ColumnDef,
  columnFilteringFeature,
  createFilteredRowModel,
  createPaginatedRowModel,
  createSortedRowModel,
  filterFn_includesString,
  globalFilteringFeature,
  RowData,
  rowPaginationFeature,
  rowSortingFeature,
  SortingState,
  sortFn_alphanumeric,
  tableFeatures,
  useTable,
} from '@tanstack/react-table';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronsUpDown,
  ChevronUp,
  Search,
} from 'lucide-react';
import { ReactNode, TdHTMLAttributes, useMemo, useState } from 'react';

import { Button } from './Button';
import { cn } from './cn';
import { NativeSelect } from './Field';
import { IconButton } from './IconButton';
import { Input } from './Input';
import { Skeleton } from './Skeleton';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableHeader, TableRow } from './Table';

export type DataTableColumn<T extends RowData> = {
  id: string;
  header: ReactNode;
  /**
   * The plain value of the cell. Sorting and search read it, and it is what the
   * cell shows unless `cell` is given. A column without it cannot be sorted or searched.
   */
  value?: (row: T) => string | number | boolean | Date | null | undefined;
  /** Custom cell content, for anything richer than the plain value. */
  cell?: (row: T) => ReactNode;
  /** Defaults to true when the table is sortable and the column has a `value`. */
  sortable?: boolean;
  align?: 'left' | 'right';
  headerClassName?: string;
  cellClassName?: string;
  /** Extra attributes for one cell, such as hover handlers. */
  cellProps?: (row: T) => TdHTMLAttributes<HTMLTableCellElement>;
};

export type DataTableSort = { id: string; desc: boolean };

export interface DataTableProps<T extends RowData> {
  columns: DataTableColumn<T>[];
  rows: readonly T[];
  getRowId?: (row: T, index: number) => string;
  /** Lets people sort by clicking a column header. */
  sortable?: boolean;
  /**
   * Pass both to own the sort order (server-side sorting): the table then shows
   * `rows` in the order given and only reports the header clicks.
   */
  sort?: DataTableSort | null;
  onSortChange?: (sort: DataTableSort | null) => void;
  /** Shows a search box that filters on every column's `value`. */
  searchable?: boolean;
  /** Rows per page. Leave unset to show every row. */
  pageSize?: number;
  striped?: boolean;
  /** Tighter cell padding, for wide tables with many columns. */
  dense?: boolean;
  /** Draws a rounded border around the table, for one that is not already inside a card. */
  bordered?: boolean;
  /**
   * Pin column headers while the body scrolls. Uses an internal max-height
   * scrollport so sticky is not broken by the page scroller.
   */
  stickyHeader?: boolean;
  onRowClick?: (row: T) => void;
  rowClassName?: (row: T) => string | undefined;
  loading?: boolean;
  error?: ReactNode;
  emptyMessage?: ReactNode;
  /** Extra rows after the data, such as totals. Rendered inside the table body. */
  footerRows?: ReactNode;
  className?: string;
  'aria-label'?: string;
}

const PAGE_SIZES = [5, 10, 25, 50, 100];
// Large enough that "unpaged" tables still show every filtered row in one page.
const UNPAGED_PAGE_SIZE = Number.MAX_SAFE_INTEGER;

// Stable feature set: a new identity on every render would rebuild row models.
const dataTableFeatures = tableFeatures({
  columnFilteringFeature,
  globalFilteringFeature,
  rowSortingFeature,
  rowPaginationFeature,
  filteredRowModel: createFilteredRowModel(),
  sortedRowModel: createSortedRowModel(),
  paginatedRowModel: createPaginatedRowModel(),
  filterFns: { includesString: filterFn_includesString },
  sortFns: { alphanumeric: sortFn_alphanumeric },
});

/** Render a cell from our column def — not via flexRender, which remounts when the cell fn identity changes. */
function renderDataTableCell<T extends RowData>(column: DataTableColumn<T> | undefined, row: T): ReactNode {
  if (!column) {
    return null;
  }
  if (column.cell) {
    return column.cell(row);
  }
  const value = column.value?.(row);
  return value instanceof Date ? value.toLocaleString() : value == null ? null : String(value);
}

/**
 * The standard table for lists of records: typed columns, optional sorting,
 * search and paging, and the loading, empty and error states.
 */
export function DataTable<T extends RowData>(props: DataTableProps<T>) {
  const {
    columns,
    rows,
    getRowId,
    sortable = false,
    sort,
    onSortChange,
    searchable = false,
    pageSize,
    striped = false,
    dense = false,
    bordered = false,
    stickyHeader = false,
    onRowClick,
    rowClassName,
    loading = false,
    error,
    emptyMessage = 'Nothing to show.',
    footerRows,
    className,
  } = props;

  const controlledSort = sort !== undefined && !!onSortChange;
  const [internalSorting, setInternalSorting] = useState<SortingState>([]);
  const sorting: SortingState = controlledSort ? (sort ? [sort] : []) : internalSorting;
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: pageSize ?? UNPAGED_PAGE_SIZE,
  });
  const paginated = pageSize !== undefined;

  // Column defs only feed sort/filter accessors. Cell markup is rendered from `columns` so
  // callers can recreate `cell` functions on data refresh without remounting cell state.
  const columnDefs = useMemo<ColumnDef<typeof dataTableFeatures, T>[]>(
    () =>
      columns.map(column => ({
        id: column.id,
        accessorFn: (row: T) => column.value?.(row) ?? null,
        enableSorting: sortable && (column.sortable ?? !!column.value),
        enableGlobalFilter: !!column.value,
      })),
    [columns, sortable],
  );

  const table = useTable({
    features: dataTableFeatures,
    data: rows as T[],
    columns: columnDefs,
    getRowId,
    state: { sorting, globalFilter: search, pagination },
    onSortingChange: updater => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      if (controlledSort) {
        onSortChange?.(next[0] ?? null);
      } else {
        setInternalSorting(next);
      }
    },
    onGlobalFilterChange: setSearch,
    onPaginationChange: setPagination,
    manualSorting: controlledSort,
    // Controlled sort only keeps one column; Shift-click multi-sort would leave
    // next[0] as the old column and flip that sort in the URL instead.
    enableMultiSort: false,
    // First click always sorts ascending, whatever the column holds.
    sortDescFirst: false,
    // Keep the page when rows refresh in place, as a polled queue does.
    autoResetPageIndex: false,
  });

  const columnById = useMemo(() => new Map(columns.map(column => [column.id, column])), [columns]);
  const visibleRows = table.getRowModel().rows;
  const filteredCount = table.getFilteredRowModel().rows.length;
  const pageCount = paginated ? Math.max(1, table.getPageCount()) : 1;
  // Filtering or a refresh can leave the current page past the end.
  const pageIndex = Math.min(pagination.pageIndex, pageCount - 1);
  if (paginated && pageIndex !== pagination.pageIndex) {
    setPagination(current => ({ ...current, pageIndex }));
  }

  const stateRow = (content: ReactNode) => (
    <TableRow>
      <TableCell colSpan={columns.length} className="px-4 py-8 text-center text-sm text-ink-muted">
        {content}
      </TableCell>
    </TableRow>
  );

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {searchable && (
        <div className="relative px-4 pt-4">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-7 top-1/2 mt-2 size-4 -translate-y-1/2 text-ink-subtle"
          />
          <Input
            type="search"
            className="pl-9"
            aria-label="Search records"
            placeholder={`Search ${rows.length} records…`}
            value={search}
            onChange={event => {
              setSearch(event.target.value);
              setPagination(current => ({ ...current, pageIndex: 0 }));
            }}
          />
        </div>
      )}
      <TableContainer
        aria-label={props['aria-label']}
        className={cn(bordered && 'rounded-auditor-lg border border-line', stickyHeader && 'max-h-table-sticky')}
      >
        <Table aria-label={props['aria-label']} aria-busy={loading || undefined}>
          <TableHeader>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  const column = columnById.get(header.column.id);
                  const sorted = header.column.getIsSorted();
                  const label = column?.header;
                  return (
                    <TableHead
                      key={header.id}
                      aria-sort={sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : undefined}
                      className={cn(
                        dense ? 'px-3' : 'px-4',
                        column?.align === 'right' && 'text-right',
                        column?.headerClassName,
                        // Opaque fill: rows scrolling under sticky headers must not show through.
                        stickyHeader && 'sticky top-0 z-local bg-canvas',
                      )}
                    >
                      {header.column.getCanSort() ? (
                        <button
                          type="button"
                          className="inline-flex cursor-pointer items-center gap-1 uppercase tracking-eyebrow hover:text-ink focus-visible:outline-2 focus-visible:outline-brand"
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {label}
                          {sorted === 'asc' ? (
                            <ChevronUp aria-hidden="true" className="size-3.5 text-ink" />
                          ) : sorted === 'desc' ? (
                            <ChevronDown aria-hidden="true" className="size-3.5 text-ink" />
                          ) : (
                            <ChevronsUpDown aria-hidden="true" className="size-3.5 text-ink-subtle" />
                          )}
                        </button>
                      ) : (
                        label
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {error
              ? stateRow(<span className="text-critical">{error}</span>)
              : loading && rows.length === 0
                ? Array.from({ length: 3 }, (_, index) => (
                    <TableRow key={index}>
                      {columns.map(column => (
                        <TableCell key={column.id} className={dense ? 'px-3 py-2' : 'px-4 py-3'}>
                          <Skeleton className="h-4 w-3/4" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : visibleRows.length === 0
                  ? stateRow(search ? `No records match "${search}".` : emptyMessage)
                  : visibleRows.map((row, index) => (
                      <TableRow
                        key={row.id}
                        className={cn(
                          'align-top',
                          striped && index % 2 === 1 && 'bg-raised',
                          onRowClick
                            && 'cursor-pointer hover:bg-brand-subtle focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand',
                          rowClassName?.(row.original),
                        )}
                        tabIndex={onRowClick ? 0 : undefined}
                        onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                        onKeyDown={
                          onRowClick
                            ? event => {
                                if (
                                  event.target === event.currentTarget
                                  && (event.key === 'Enter' || event.key === ' ')
                                ) {
                                  event.preventDefault();
                                  onRowClick(row.original);
                                }
                              }
                            : undefined
                        }
                      >
                        {row.getAllCells().map(cell => {
                          const column = columnById.get(cell.column.id);
                          const { className: extraClassName, ...cellProps } = column?.cellProps?.(row.original) ?? {};
                          return (
                            <TableCell
                              key={cell.id}
                              {...cellProps}
                              className={cn(
                                dense ? 'px-3 py-2' : 'px-4 py-3',
                                column?.align === 'right' && 'text-right',
                                column?.cellClassName,
                                extraClassName,
                              )}
                            >
                              {renderDataTableCell(column, row.original)}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
            {footerRows}
          </TableBody>
        </Table>
      </TableContainer>
      {paginated && filteredCount > 0 && (
        <DataTablePagination
          pageIndex={pageIndex}
          pageCount={pageCount}
          pageSize={pagination.pageSize}
          onPageChange={next => setPagination(current => ({ ...current, pageIndex: next }))}
          onPageSizeChange={next => setPagination({ pageIndex: 0, pageSize: next })}
        />
      )}
    </div>
  );
}

const DataTablePagination = (props: {
  pageIndex: number;
  pageCount: number;
  pageSize: number;
  onPageChange: (pageIndex: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}) => {
  const { pageIndex, pageCount, pageSize, onPageChange, onPageSizeChange } = props;
  const firstVisiblePage = Math.max(0, Math.min(pageIndex - 2, pageCount - 5));
  const visiblePages = Array.from({ length: Math.min(5, pageCount) }, (_, index) => firstVisiblePage + index);
  const sizes = PAGE_SIZES.includes(pageSize) ? PAGE_SIZES : [...PAGE_SIZES, pageSize].sort((a, b) => a - b);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 pb-4">
      <label className="m-0 flex items-center gap-2 whitespace-nowrap text-xs text-ink-muted">
        Rows per page
        <NativeSelect
          wrapperClassName="w-20"
          value={pageSize}
          onChange={event => onPageSizeChange(Number(event.target.value))}
        >
          {sizes.map(size => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </NativeSelect>
      </label>
      <nav aria-label="Table pages" className="flex items-center gap-1">
        <IconButton label="First page" onClick={() => onPageChange(0)} disabled={pageIndex === 0}>
          <ChevronsLeft className="size-4" />
        </IconButton>
        <IconButton label="Previous page" onClick={() => onPageChange(pageIndex - 1)} disabled={pageIndex === 0}>
          <ChevronLeft className="size-4" />
        </IconButton>
        {visiblePages.map(page => (
          <Button
            key={page}
            size="icon"
            variant={page === pageIndex ? 'primary' : 'ghost'}
            aria-current={page === pageIndex ? 'page' : undefined}
            aria-label={`Page ${page + 1}`}
            onClick={() => onPageChange(page)}
          >
            {page + 1}
          </Button>
        ))}
        <IconButton label="Next page" onClick={() => onPageChange(pageIndex + 1)} disabled={pageIndex >= pageCount - 1}>
          <ChevronRight className="size-4" />
        </IconButton>
        <IconButton label="Last page" onClick={() => onPageChange(pageCount - 1)} disabled={pageIndex >= pageCount - 1}>
          <ChevronsRight className="size-4" />
        </IconButton>
      </nav>
    </div>
  );
};
